<template>
  <div class="media-player">
    <div v-if="media.cover_url && media.kind === 'audio'" class="media-player-cover">
      <site-media :source="media.cover_url" :alt="media.cover_alt || media.title" loading="eager" />
    </div>
    <audio
      v-if="media.kind === 'audio' && playbackUrl && !playbackFailed"
      ref="audioElement"
      :key="playbackAttempt"
      class="media-player-audio"
      :src="playbackUrl"
      controls
      :autoplay="autoplay"
      preload="metadata"
      @error="playbackFailed = true"
    ></audio>
    <video
      v-else-if="media.kind === 'video' && playbackUrl && isDirectMediaUrl && !playbackFailed"
      ref="videoElement"
      :key="playbackAttempt"
      class="media-player-video"
      :src="playbackUrl"
      :poster="media.cover_url || undefined"
      controls
      :autoplay="autoplay"
      playsinline
      preload="metadata"
      @error="playbackFailed = true"
    ></video>
    <iframe
      v-else-if="media.kind === 'video' && embedUrl"
      class="media-player-embed"
      :src="embedUrl"
      :title="media.title"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
      allowfullscreen
      referrerpolicy="strict-origin-when-cross-origin"
    ></iframe>
    <div v-else class="media-player-external" :role="playbackFailed ? 'alert' : 'status'">
      <i
        :class="media.kind === 'audio' ? 'i-lucide-headphones' : 'i-lucide-play-circle'"
        aria-hidden="true"
      ></i>
      <strong>
        {{ playbackFailed ? '暂时无法播放' : playbackUrl ? '请前往原站播放' : '暂无可用播放源' }}
      </strong>
      <button v-if="playbackFailed" class="media-player-retry" type="button" @click="retryPlayback">
        重新加载
      </button>
      <a v-if="playbackUrl" :href="playbackUrl" target="_blank" rel="noopener">
        打开播放源
        <i class="i-lucide-external-link" aria-hidden="true"></i>
      </a>
    </div>
    <p v-if="embedUrl" class="media-player-source">
      <a :href="playbackUrl" target="_blank" rel="noopener noreferrer">在原站播放</a>
    </p>
    <p v-if="media.credit" class="media-player-credit">来源：{{ media.credit }}</p>
  </div>
</template>

<script setup lang="ts">
  import type { MediaItem } from '~/types/site-api'

  defineOptions({ name: 'MediaPlayer' })

  const props = withDefaults(
    defineProps<{
      autoplay?: boolean
      media: MediaItem
    }>(),
    { autoplay: false }
  )

  const sourceUrl = computed(
    () =>
      props.media.playback_url ||
      props.media.sources?.find((source) => source.is_primary)?.url ||
      props.media.sources?.[0]?.url ||
      ''
  )
  const playbackUrl = computed(() => {
    try {
      const url = new URL(sourceUrl.value)
      return ['http:', 'https:'].includes(url.protocol) ? url.href : ''
    } catch {
      return sourceUrl.value.startsWith('/') && !sourceUrl.value.startsWith('//')
        ? sourceUrl.value
        : ''
    }
  })
  const embedUrl = computed(() => {
    if (!playbackUrl.value || props.media.kind !== 'video') return ''
    let source: URL
    try {
      source = new URL(playbackUrl.value)
    } catch {
      return ''
    }
    const host = source.hostname.toLowerCase()
    if (
      ['bilibili.com', 'm.bilibili.com', 'player.bilibili.com', 'www.bilibili.com'].includes(host)
    ) {
      const bvid =
        source.pathname.match(/\/video\/(BV[\da-z]+)/i)?.[1] || source.searchParams.get('bvid')
      const aid = source.pathname.match(/\/video\/av(\d+)/i)?.[1] || source.searchParams.get('aid')
      const embed = new URL('https://player.bilibili.com/player.html')
      if (bvid && /^BV[\da-z]+$/i.test(bvid)) embed.searchParams.set('bvid', bvid)
      else if (aid && /^\d+$/.test(aid)) embed.searchParams.set('aid', aid)
      else return ''
      const page = source.searchParams.get('p') || source.searchParams.get('page')
      if (page && /^[1-9]\d*$/.test(page)) embed.searchParams.set('page', page)
      embed.searchParams.set('autoplay', props.autoplay ? '1' : '0')
      return embed.href
    }
    if (
      [
        'm.youtube.com',
        'www.youtube-nocookie.com',
        'www.youtube.com',
        'youtu.be',
        'youtube.com',
      ].includes(host)
    ) {
      const id =
        host === 'youtu.be'
          ? source.pathname.split('/')[1]
          : source.searchParams.get('v') ||
            source.pathname.match(/^\/(?:embed|shorts)\/([\w-]+)/)?.[1]
      if (id && /^[\w-]{11}$/.test(id)) {
        const embed = new URL(`https://www.youtube-nocookie.com/embed/${id}`)
        embed.searchParams.set('autoplay', props.autoplay ? '1' : '0')
        return embed.href
      }
    }
    return ''
  })
  const isDirectMediaUrl = computed(
    () =>
      !embedUrl.value &&
      (/\.(?:mp4|webm|ogg|ogv|mov|m4v)(?:[?#]|$)/i.test(playbackUrl.value) ||
        (props.media.sources?.some(
          (source) => source.url === sourceUrl.value && source.mime_type?.startsWith('video/')
        ) ??
          false))
  )
  const videoElement = useTemplateRef<HTMLVideoElement>('videoElement')
  const audioElement = useTemplateRef<HTMLAudioElement>('audioElement')
  const playbackFailed = shallowRef(false)
  const playbackAttempt = shallowRef(0)

  function retryPlayback() {
    playbackFailed.value = false
    playbackAttempt.value += 1
  }

  watch(playbackUrl, retryPlayback)
  onBeforeUnmount(() => {
    videoElement.value?.pause()
    audioElement.value?.pause()
  })
</script>

<style scoped lang="scss">
  .media-player {
    overflow: hidden;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
  }
  .media-player-cover {
    aspect-ratio: 16 / 8;
    max-height: 480px;
  }
  .media-player-cover :deep(img) {
    object-fit: cover;
  }
  .media-player-audio {
    display: block;
    width: calc(100% - 48px);
    margin: 28px 24px;
  }
  .media-player-video {
    display: block;
    width: 100%;
    max-height: min(65dvh, 620px);
    aspect-ratio: 16 / 9;
    object-fit: contain;
    background: #0a0a0b;
  }
  .media-player-embed {
    display: block;
    width: 100%;
    max-height: 65dvh;
    aspect-ratio: 16 / 9;
    background: #0a0a0b;
    border: 0;
  }
  .media-player-source {
    padding: 12px 24px;
    margin: 0;
    font-size: 13px;
    text-align: right;
  }
  .media-player-source a {
    color: var(--accent);
    cursor: pointer;
  }
  .media-player-retry {
    padding: 8px 16px;
    color: var(--accent);
    cursor: pointer;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 4px;
  }
  .media-player-external {
    display: grid;
    gap: 10px;
    place-items: center;
    padding: 48px 24px;
    text-align: center;
  }
  .media-player-external > i {
    width: 42px;
    height: 42px;
    color: var(--accent);
  }
  .media-player-external p {
    margin: 0;
    color: var(--muted);
  }
  .media-player-external a {
    display: inline-flex;
    gap: 5px;
    align-items: center;
    color: var(--accent);
    cursor: pointer;
  }
  .media-player-external a i {
    width: 15px;
    height: 15px;
  }
  .media-player-credit {
    padding: 0 24px 22px;
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  @media (max-width: 767px) {
    .media-player-cover {
      aspect-ratio: 16 / 10;
    }
    .media-player-audio {
      width: calc(100% - 32px);
      margin: 20px 16px;
    }
    .media-player-credit {
      padding-inline: 16px;
    }
  }
</style>
